import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { globalRealtimeManager } from "@/services/globalRealtimeManager";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { format, isToday, isYesterday } from "date-fns";
import { AlphaLoader } from "@/components/ui/alpha-loader";

interface Conversation {
  conversationId: string;
  partnerId: string;
  partnerName: string;
  partnerPhoto?: string;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  isOnline: boolean;
}

const ConversationList = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef<any>(null);

  useEffect(() => {
    if (user) {
      loadConversations();
      setupRealtimeUpdates();
    }

    return () => {
      if (channelRef.current && channelRef.current.unsubscribe) {
        try {
          channelRef.current.unsubscribe();
        } catch (error) {
          console.warn('Error cleaning up channel:', error);
        }
        channelRef.current = null;
      }
    };
  }, [user]);

  const loadConversations = async () => {
    if (!user) return;

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Load timeout')), 15000); // 15 second timeout
    });

    try {
      console.log('📨 [ConversationList] Loading conversations for user:', user.id);
      
      // Get all messages where user is either sender or receiver with timeout
      const messagesPromise = supabase
        .from('messages')
        .select('*')
        .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      const { data: messages, error: messagesError } = await Promise.race([
        messagesPromise,
        timeoutPromise
      ]) as any;

      if (messagesError) throw messagesError;

      if (!messages || messages.length === 0) {
        console.log('📨 [ConversationList] No messages found');
        setConversations([]);
        setLoading(false);
        return;
      }

      console.log('📨 [ConversationList] Found', messages.length, 'messages');

      // Group messages by conversation partner
      const conversationMap = new Map<string, any>();

      for (const message of messages) {
        const partnerId = message.sender_id === user.id ? message.receiver_id : message.sender_id;
        const conversationId = [user.id, partnerId].sort().join('-');

        if (!conversationMap.has(partnerId)) {
          conversationMap.set(partnerId, {
            partnerId,
            conversationId,
            lastMessage: message,
            messages: []
          });
        }

        conversationMap.get(partnerId).messages.push(message);
      }

      // Filter out blocked users
      const initialPartnerIds = Array.from(conversationMap.keys());
      if (initialPartnerIds.length > 0) {
        try {
          // Get all blocks where current user is either blocker or blocked
          const { data: blocks } = await supabase
            .from('user_blocks')
            .select('blocker_id, blocked_id')
            .or(`blocker_id.eq.${user.id},blocked_id.eq.${user.id}`);

          if (blocks && blocks.length > 0) {
            const blockedUserIds = new Set<string>();
            
            blocks.forEach(block => {
              // Add both blocked users and users who blocked current user
              if (block.blocker_id === user.id) {
                blockedUserIds.add(block.blocked_id);
              } else if (block.blocked_id === user.id) {
                blockedUserIds.add(block.blocker_id);
              }
            });

            // Remove blocked conversations
            blockedUserIds.forEach(blockedId => {
              conversationMap.delete(blockedId);
            });

            console.log('📨 [ConversationList] Filtered out', blockedUserIds.size, 'blocked conversations');
          }
        } catch (blockError) {
          console.warn('⚠️ [ConversationList] Error checking blocks:', blockError);
          // Continue with all conversations if block check fails
        }
      }

      // Get partner profiles and create conversation objects
      const partnerIds = Array.from(conversationMap.keys());
      
      if (partnerIds.length === 0) {
        setConversations([]);
        setLoading(false);
        return;
      }

      console.log('📨 [ConversationList] Loading data for', partnerIds.length, 'partners');

      // Load conversation data with individual error handling
      const conversationsData: Conversation[] = [];
      
      for (const conv of Array.from(conversationMap.values())) {
        try {
          // Get unread count
          const unreadCount = conv.messages.filter((msg: any) => 
            msg.sender_id === conv.partnerId && 
            msg.receiver_id === user.id && 
            !msg.is_read
          ).length;

          // Get partner profile with error handling
          let partnerProfile = null;
          try {
            const { data } = await supabase
              .from('user_profiles')
              .select('display_name, first_name, last_name, photo_urls')
              .eq('user_id', conv.partnerId)
              .maybeSingle();
            partnerProfile = data;
          } catch (profileError) {
            console.warn('⚠️ [ConversationList] Failed to load profile for', conv.partnerId, profileError);
          }

          // Get partner online status with error handling
          let partnerUser = null;
          try {
            const { data } = await supabase
              .from('users')
              .select('last_seen')
              .eq('id', conv.partnerId)
              .maybeSingle();
            partnerUser = data;
          } catch (userError) {
            console.warn('⚠️ [ConversationList] Failed to load user data for', conv.partnerId, userError);
          }

          const partnerName = partnerProfile?.display_name || 
                              `${partnerProfile?.first_name || ''} ${partnerProfile?.last_name || ''}`.trim() || 
                              'Unknown User';

          const isOnline = partnerUser?.last_seen && 
                          new Date(partnerUser.last_seen).getTime() > Date.now() - 5 * 60 * 1000;

          conversationsData.push({
            conversationId: conv.conversationId,
            partnerId: conv.partnerId,
            partnerName,
            partnerPhoto: partnerProfile?.photo_urls?.[0],
            lastMessage: conv.lastMessage?.content || 'Start a conversation...',
            lastMessageTime: conv.lastMessage?.created_at || new Date().toISOString(),
            unreadCount,
            isOnline: !!isOnline
          });

        } catch (convError) {
          console.warn('⚠️ [ConversationList] Failed to load conversation data for partner', conv.partnerId, convError);
          
          // Still add conversation with minimal data
          conversationsData.push({
            conversationId: conv.conversationId,
            partnerId: conv.partnerId,
            partnerName: 'Unknown User',
            partnerPhoto: undefined,
            lastMessage: conv.lastMessage?.content || 'Start a conversation...',
            lastMessageTime: conv.lastMessage?.created_at || new Date().toISOString(),
            unreadCount: 0,
            isOnline: false
          });
        }
      }

      // Sort by last message time
      conversationsData.sort((a, b) => 
        new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime()
      );

      console.log('📨 [ConversationList] Successfully loaded', conversationsData.length, 'conversations');
      setConversations(conversationsData);
      
    } catch (error) {
      console.error('❌ [ConversationList] Error loading conversations:', error);
      
      // Show user-friendly error but don't leave them stuck
      if (error instanceof Error && error.message === 'Load timeout') {
        console.error('❌ [ConversationList] Loading timed out after 15 seconds');
      }
      
      // Set empty conversations so user isn't stuck on loading screen
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeUpdates = () => {
    if (!user) return;

    // Use global realtime manager instead of creating own channel
    const unsubscribe = globalRealtimeManager.subscribeToConversations(user.id, () => {
      console.log(`📨 [ConversationList] Realtime update received`);
      loadConversations();
    });

    // Store unsubscribe function for cleanup
    channelRef.current = { unsubscribe };
  };

  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    
    if (isToday(date)) {
      return format(date, 'HH:mm');
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else {
      return format(date, 'dd/MM');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <AlphaLoader size="mini" />
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="text-center p-8">
        <MessageCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Conversations</h3>
        <p className="text-muted-foreground">Start discovering users to begin conversations!</p>
        <Link
          to="/discovery"
          className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 mt-4"
        >
          Start Discovering
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {conversations.map((conversation) => (
        <Link key={conversation.conversationId} to={`/messages/${conversation.conversationId}`}>
          <Card className="hover:bg-gray-50 transition-colors cursor-pointer bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={conversation.partnerPhoto} />
                    <AvatarFallback className="bg-secondary text-foreground">
                      {conversation.partnerName.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {conversation.isOnline && (
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-card rounded-full"></div>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-foreground truncate">
                      {conversation.partnerName}
                    </h3>
                    <div className="flex items-center space-x-2">
                      {conversation.unreadCount > 0 && (
                        <Badge variant="outline" className="bg-primary text-primary-foreground text-xs border-primary">
                          {conversation.unreadCount}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatMessageTime(conversation.lastMessageTime)}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground truncate">
                    {conversation.lastMessage}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
};

export default ConversationList;
